import { Inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { has, sortBy } from 'lodash';
import { Observable, Subject, filter, take } from 'rxjs';

import { DOCUMENT_CATEGORIES } from '@lockton-apps/shared/util/constants';
import {
  UPLOAD_CONFIG,
  UploadConfig,
} from '@lockton-apps/shared/util/upload-config';

import { AdminDocumentUploadDialogComponent } from './advanced/admin-document-upload-dialog.component';
import { ContactDocumentUploadDialogComponent } from './basic/contact-document-upload-dialog.component';
import {
  AdminDocumentDialogData,
  DocumentDialogData,
  DocumentDialogResult,
  DocumentDialogResultWithFolder,
} from './models';

@Injectable({
  providedIn: 'root',
})
export class DocumentsUploadDialogService {
  _submitted = new Subject<
    DocumentDialogResult | DocumentDialogResultWithFolder
  >();
  private _maxWidth = 800;

  constructor(
    private dialog: MatDialog,
    @Inject(UPLOAD_CONFIG) private uploadConfig: UploadConfig
  ) {}

  openAdvanced(options: AdminDocumentDialogData) {
    this.dialog
      .open<AdminDocumentUploadDialogComponent, AdminDocumentDialogData>(
        AdminDocumentUploadDialogComponent,
        {
          data: {
            ...options,
            categoryOptions:
              options.categoryOptions || sortBy([...DOCUMENT_CATEGORIES]),
            acceptedFileTypes: this.uploadConfig.acceptedFileTypes,
            acceptedFileTypesDescription:
              this.uploadConfig.acceptedFileTypesDescription,
          },
          maxWidth: this._maxWidth,
          width: '100%',
        }
      )
      .afterClosed()
      .pipe(take(1))
      .subscribe((dialogResult) => {
        if (has(dialogResult, 'documents')) {
          this._submitted.next(dialogResult);
        }
      });
  }

  openBasic(options: DocumentDialogData) {
    this.dialog
      .open<ContactDocumentUploadDialogComponent, DocumentDialogData>(
        ContactDocumentUploadDialogComponent,
        {
          data: {
            ...options,
            acceptedFileTypes: this.uploadConfig.acceptedFileTypes,
            acceptedFileTypesDescription:
              this.uploadConfig.acceptedFileTypesDescription,
          },
          maxWidth: this._maxWidth,
          width: '100%',
        }
      )
      .afterClosed()
      .pipe(take(1))
      .subscribe((dialogResult) => {
        if (has(dialogResult, 'documents')) {
          this._submitted.next(dialogResult);
        }
      });
  }

  documentsSubmitted$() {
    return this._submitted
      .asObservable()
      .pipe(
        filter((dialogResult) => !has(dialogResult, 'folderName'))
      ) as Observable<DocumentDialogResult>;
  }

  folderAndDocumentsSubmitted$() {
    return this._submitted
      .asObservable()
      .pipe(
        filter((dialogResult) => has(dialogResult, 'folderName'))
      ) as Observable<DocumentDialogResultWithFolder>;
  }
}
